import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import ProfileImage from '../../Header/ProfileImage';
import { normalise } from '../access/accessModel';

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  onMentionQuery?: (query: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  showSuggestions?: boolean;
  suggestions?: Array<{ id: string; first_name: string; last_name: string; email: string; profile_picture?: string | null }>;
  onSuggestionSelect?: (memberId: string, memberName: string) => void;
  selectedSuggestionIndex?: number;
  onSelectedIndexChange?: (index: number) => void;
}

const MentionInput: React.FC<MentionInputProps> = ({
  value,
  onChange,
  onMentionQuery,
  onSubmit,
  placeholder,
  rows = 3,
  className = '',
  showSuggestions = false,
  suggestions = [],
  onSuggestionSelect,
  selectedSuggestionIndex = 0,
  onSelectedIndexChange,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    onChange(text);

    // Check for mention query — trigger when user types @
    const lastAtIndex = text.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const afterAt = text.substring(lastAtIndex + 1);
      // Only trigger if it's not inside a completed @[Name] mention
      if (!afterAt.includes('[') || afterAt.lastIndexOf('[') < afterAt.lastIndexOf(']')) {
        const query = afterAt.split(/[\s\n]/)[0].toLowerCase();
        onMentionQuery?.(query);
      }
    } else {
      onMentionQuery?.('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Handle Enter key to submit (unless suggestions are open)
    if (e.key === 'Enter' && !e.shiftKey && !showSuggestions) {
      e.preventDefault();
      onSubmit?.();
      return;
    }

    // Handle arrow keys for suggestion navigation
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      onSelectedIndexChange?.((selectedSuggestionIndex + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      onSelectedIndexChange?.((selectedSuggestionIndex - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' && showSuggestions) {
      const suggestion = suggestions[selectedSuggestionIndex];
      if (suggestion) {
        e.preventDefault();
        onSuggestionSelect?.(suggestion.id, `${suggestion.first_name} ${suggestion.last_name}`);
      }
    } else if (e.key === 'Escape') {
      // Allow parent to handle escape
    }
  };

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        rows={rows}
        className={clsx(
          'w-full p-3 rounded-md border bg-surface-sunken/70 text-sm text-fg-body placeholder-fg-muted resize-none focus:outline-none transition-colors duration-200',
          isFocused ? 'border-champagne' : 'border-hairline-soft hover:border-hairline',
          className
        )}
        spellCheck="true"
      />

      {/* Mention suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass absolute bottom-full left-3 z-50 mb-2 max-h-48 w-64 overflow-y-auto rounded-lg shadow-sm"
        >
          {suggestions.map((member, idx) => (
            <button
              key={member.id}
              onClick={() => {
                onSuggestionSelect?.(member.id, `${member.first_name} ${member.last_name}`);
              }}
              className={clsx(
                'flex w-full items-center gap-2 px-3 py-2 text-left transition-colors',
                selectedSuggestionIndex === idx
                  ? 'bg-champagne/10 text-champagne'
                  : 'hover:bg-surface-sunken/70'
              )}
            >
              <ProfileImage
                profilePicture={normalise(member).avatar}
                email={member.email}
                initials={`${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase()}
                size="w-5 h-5"
              />
              <span className="truncate text-sm text-fg-body">
                {member.first_name} {member.last_name}
              </span>
            </button>
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default MentionInput;
